import React from 'react';
import _ from 'lodash';

import {
  AdminQueuingActivityTemplate,
  routeDirectory,
  selectFields,
  useTabSearchParams
} from 'modules';
import {
  Box,
  Button,
  TextField
} from '@mui/material';
import {
  useNavigate,
  useParams
} from 'react-router-dom';

import { SalesChannelName } from 'generated/graphql';
import { DateTime } from 'luxon';
import * as TabBar from './TabBar/TabBar';
import * as NewOrderTable from './NewOrderTable/NewOrderTable';
import * as Table from './Table/Table';
import * as shared from './shared';
import * as types from './types';



export const SalesOrdersActivity: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{
    salesChannel: SalesChannelName;
    tabId: shared.ITabId;
  }>();
  const { FC: StoreSelectField } = selectFields.StoreSelectField.genComponentAndControllerGenerator({
    salesChannel: params.salesChannel
  });

  const [searchText, setSearchText] = React.useState('');
  const [storeId, setStoreId] = useTabSearchParams({
    defaultTabId: '',
    paramName: 'storeId'
  });



  const debouncedSetSearchText = React.useMemo(
    () => _.debounce((searchText: string) => {
      setSearchText(searchText);
    }, 1000),
    [setSearchText]
  );



  const filter = React.useMemo(
    (): types.IFilter => ({
      salesChannel: params.salesChannel ?? 'ecommerce',
      storeId: storeId || '',
      tabId: params.tabId ?? 'newSalesOrders',
      searchText,
      after: DateTime.fromISO('2025-03-28').startOf('day')// DateTime.now().minus({ days: 90 }).startOf('day')
    }),
    [
      params.salesChannel,
      params.tabId,
      searchText,
      storeId
    ]
  );



  const handleClickViewAllSalesOrders = React.useCallback(
    () => {
      navigate(routeDirectory.admin.reconciliationReport.path(filter.salesChannel));
    },
    [
      filter.salesChannel,
      navigate
    ]
  );

  return (
    <AdminQueuingActivityTemplate.FC
      elTabBar={(
        <TabBar.FC filter={filter} />
      )}
      elButtons={<div />}
      elTabBarActions={
        <>
          <Box sx={{
            flex: 4,
            display: 'flex',
            minWidth: 368,
            gap: '8px'
          }}>
            <TextField
              label="Search"
              onChange={ev => debouncedSetSearchText(ev.target.value ?? '')} />

            <StoreSelectField
              disableDefaultMargins
              label="Store"
              selectProps={{
                type: 'singleSelect',
                value: {
                  id: storeId ?? '',
                  displayName: ''
                },
                onChange: store => setStoreId(store?.id ?? '')
              }} />
          </Box>

          <Box>
            <Button
              color="info"
              variant="outlined"
              onClick={handleClickViewAllSalesOrders}>
              View All
            </Button>
          </Box>
        </>
      }>

      {params.tabId === 'newSalesOrders' ? (
        <NewOrderTable.FC filter={filter} />
      ) : (
        <Table.FC
          filter={filter}
          columnsToRender={__tabId2Columns[filter.tabId]} />
      )}
    </AdminQueuingActivityTemplate.FC >
  );
};



const __defaultColumns: Table.IColumnId[] = [
  'orderNumber',
  'orderDate',
  'orderAge',
  'store'
];

const __tabId2Columns: Record<types.ITabId, Table.IColumnId[]> = {
  awaitingPayment: [
    ...__defaultColumns,
    'orderTotal',
    'paymentMethod',
    'customerEmail'
  ],
  awaitingApproval: [
    ...__defaultColumns,
    'orderTotal',
    'customerEmail',
    'netSuiteSalesOrderRecord',
    'netSuiteCustomerRecord',
    'e1Record'
  ],
  awaitingFulfillment: [
    ...__defaultColumns,
    'netSuiteSalesOrderRecord',
    'netSuiteCustomerRecord',
    'e1Record',
    'shipment',
    'quantity',
    'itemFulfillmentNetSuiteRecord'
  ],
  inTransit: [
    ...__defaultColumns,
    'itemFulfillmentNetSuiteRecord',
    'shipment'
  ],
  // Not used
  newSalesOrders: []
};
